import { CodeSets } from '@chiroup/core/types/BillingCode.type';
import { SandboxClinicalData } from '@chiroup/core/types/SandboxClinicalData.type';

import extensionBiasedImage from './images/extensionBiased.png';
import lowerCrossedImage from './images/lowerCrossed.png';
import lumbarDiscImage from './images/lumbarDisc.jpg';
import lumbarFacetImage from './images/lumbarFacet.png';
import lumbarJointImage from './images/lumbarJoint.jpeg';
import lumbarSciaticImage from './images/lumbarSciatic.png';
import lumbarSpondylosisImage from './images/lumbarSpondylosis.jpg';
import lumbarSprainImage from './images/lumbarSprain.jpg';
import sacroiliacJointImage from './images/sacroiliacJoint.jpeg';
import spinalInstabilityImage from './images/spinalInstability.jpeg';

// Published stock protocols, captured and cross-checked against the signed-in library 2026-10-15.
// Card thumbnails come from commonImg (the list endpoint's image), not the clinical img field.
// No clinic customizations or patient data; reference codes retain the existing first-code fallback.
const publicClinicalData: SandboxClinicalData = {
  conditions: [
    {
      ID: 4932379,
      image: lumbarDiscImage,
      name: 'Lumbar Disc Lesion',
      commonName: 'Lumbar Disc',
      regions: ['L'],
      muscles: [41, 74, 118, 125],
      typicallyPositiveEvals: [29, 31, 33, 152],
      typicallyNegativeEvals: [12, 25, 54, 55],
      referenceCodes: [
        {
          code: 'M5116',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Intervertebral disc disorders with radiculopathy, lumbar region',
        },
      ],
      favorite: false,
    },
    {
      ID: 4887356,
      image: lumbarFacetImage,
      name: 'Lumbar Facet Syndrome',
      commonName: 'Lumbar Facet Syndrome',
      regions: ['L'],
      muscles: [41, 74, 118, 125],
      typicallyPositiveEvals: [54, 55, 122, 138],
      typicallyNegativeEvals: [12, 25, 33],
      referenceCodes: [
        {
          code: 'M4606',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Spinal enthesopathy, lumbar region',
        },
      ],
      favorite: false,
    },
    {
      ID: 4983570,
      image: lumbarSciaticImage,
      name: 'Lumbar Radiculopathy - Sciatic',
      commonName: 'Sciatica',
      regions: ['L'],
      muscles: [41, 125],
      typicallyPositiveEvals: [29, 33],
      typicallyNegativeEvals: [914],
      referenceCodes: [
        {
          code: 'M25551',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Pain in right hip',
        },
      ],
      favorite: false,
    },
    {
      ID: 4983673,
      image: lumbarJointImage,
      name: 'Lumbar Segmental Joint Dysfunction',
      commonName: 'Lumbar Joint Restriction',
      regions: ['L'],
      muscles: [41, 74, 118, 125],
      typicallyPositiveEvals: [54, 55, 122, 138],
      typicallyNegativeEvals: [12, 25, 33],
      referenceCodes: [
        {
          code: 'M25551',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Pain in right hip',
        },
      ],
      favorite: false,
    },
    {
      ID: 4944801,
      image: lumbarSpondylosisImage,
      name: 'Lumbar Spondylosis/DJD/DDD',
      commonName: 'Lumbar Arthritis',
      regions: ['L'],
      muscles: [41, 74, 118, 125],
      typicallyPositiveEvals: [54, 55, 74, 122],
      typicallyNegativeEvals: [12, 25, 29, 33],
      referenceCodes: [
        {
          code: 'M4726',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Other spondylosis with radiculopathy, lumbar region',
        },
      ],
      favorite: false,
    },
    {
      ID: 4315371,
      image: lumbarSprainImage,
      name: 'Lumbar Sprain/Strain',
      commonName: 'Low Back Strain',
      regions: ['L'],
      muscles: [41, 74, 118, 125],
      typicallyPositiveEvals: [33, 122, 138, 139],
      typicallyNegativeEvals: [12, 25, 29, 108],
      referenceCodes: [
        {
          code: 'S335XXA',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Sprain of ligaments of lumbar spine, initial encounter',
        },
      ],
      favorite: false,
    },
    {
      ID: 4961447,
      image: sacroiliacJointImage,
      name: 'Sacroiliac Joint Dysfunction',
      commonName: 'Sacroiliac Joint Dysfunction',
      regions: ['H', 'L'],
      muscles: [39, 67, 118, 125],
      typicallyPositiveEvals: [35, 36, 37, 38],
      typicallyNegativeEvals: [33],
      referenceCodes: [
        {
          code: 'M25551',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Pain in right hip',
        },
      ],
      favorite: false,
    },
    {
      ID: 4933376,
      image: spinalInstabilityImage,
      name: 'Spinal Instability',
      commonName: 'Core Weakness',
      regions: ['H', 'L'],
      muscles: [41, 74, 125],
      typicallyPositiveEvals: [142, 666, 668],
      typicallyNegativeEvals: [],
      referenceCodes: [
        {
          code: 'M532X6',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Spinal instabilities, lumbar region',
        },
      ],
      favorite: false,
    },
    {
      ID: 4314230,
      image: extensionBiasedImage,
      name: 'Extension Biased LBP',
      commonName: 'Extension Biased LBP',
      regions: ['H', 'L'],
      muscles: [74, 125],
      typicallyPositiveEvals: [900],
      typicallyNegativeEvals: [],
      referenceCodes: [
        {
          code: 'M5450',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Low back pain, unspecified',
        },
      ],
      favorite: false,
    },
    {
      ID: 4316816,
      image: lowerCrossedImage,
      name: 'Lower Crossed Syndrome',
      commonName: 'Lower Crossed Syndrome',
      regions: ['F/A', 'H', 'K', 'L', 'O', 'T'],
      muscles: [41, 79, 118, 125],
      typicallyPositiveEvals: [13, 78, 124],
      typicallyNegativeEvals: [],
      referenceCodes: [
        {
          code: 'M9903',
          codeSet: CodeSets.ICD10CM,
          favorite: 0,
          order: 1,
          description: 'Segmental and somatic dysfunction of lumbar region',
        },
      ],
      favorite: false,
    },
  ],
  muscles: [
    {
      ID: 39,
      name: 'Hamstring/ Biceps femoris',
      muscleGroup: false,
      regions: ['H', 'K'],
    },
    {
      ID: 41,
      name: 'Iliopsoas',
      muscleGroup: false,
      regions: ['H', 'L'],
    },
    {
      ID: 67,
      name: 'Piriformis',
      muscleGroup: false,
      regions: ['H', 'L'],
    },
    {
      ID: 74,
      name: 'Quadratus lumborum',
      muscleGroup: false,
      regions: ['L', 'T'],
    },
    {
      ID: 79,
      name: 'Rectus femoris',
      muscleGroup: false,
      regions: ['H', 'L'],
    },
    {
      ID: 118,
      name: 'Gluteal muscles',
      muscleGroup: true,
      regions: ['H', 'L'],
    },
    {
      ID: 125,
      name: 'Lumbar erectors',
      muscleGroup: true,
      regions: ['L'],
    },
  ],
  evaluations: [
    {
      ID: 12,
      name: 'FABER Test',
      regions: ['H'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 13,
      name: 'Trendelenburg Sign',
      regions: ['H', 'L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 25,
      name: 'FADIR Test',
      regions: ['H'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 29,
      name: 'Straight Leg Raise',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 31,
      name: 'Braggard Test',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 33,
      name: 'Modified Slump Test',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 35,
      name: 'Sacroiliac Distraction',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 36,
      name: 'Thigh Thrust',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 37,
      name: 'Sacroiliac Compression',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 38,
      name: 'Sacral Thrust',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 54,
      name: 'Kemps Test',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 55,
      name: 'Yeoman Test',
      regions: ['H', 'L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 74,
      name: 'Sphinx Test',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 78,
      name: 'Thomas Test',
      regions: ['H', 'L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 108,
      name: 'Valsalva Maneuver',
      regions: ['C', 'L', 'T'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 122,
      name: 'PA Shear',
      regions: ['L', 'T'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 124,
      name: 'Single Leg Squat Test',
      regions: ['F/A', 'H', 'K', 'L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 138,
      name: 'Spring Test',
      regions: ['C', 'L', 'T'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 139,
      name: 'Segmental Rotation Test',
      regions: ['C', 'L', 'T'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 142,
      name: 'Prone Instability Test',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 152,
      name: 'Milgrams Test',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 666,
      name: 'Instability Catch Sign',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 668,
      name: 'Painful Catch Sign',
      regions: ['L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 900,
      name: 'Centralization on Repetitive Lumbar Extension',
      regions: ['F/A', 'H', 'HEAD', 'K', 'L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
    {
      ID: 914,
      name: 'Signs of Cauda Equina',
      regions: ['H', 'L'],
      hide: 0,
      img: null,
      descr: '',
      video: null,
      parentID: null,
      spanishDescr: null,
      spanishName: null,
      spanishVideo: null,
    },
  ],
};

export default publicClinicalData;
